import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, Filter, Trash2, Factory, Truck, FileText, Users, AlertTriangle, X, Plus,
  ChevronRight, FilePlus2, Save, Link2, Receipt,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { useSalesOrdersSync, notifySalesOrdersChanged } from '../hooks/useSalesOrdersSync';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import CodeLabel from '../components/CodeLabel';
import ProductCodeBadge from '../components/ProductCodeBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { key: 'pending',       label: 'Pending',       color: 'text-red-300',    bg: 'bg-red-500/15 border-red-500/30' },
  { key: 'planning',      label: 'Planning',      color: 'text-amber-300',  bg: 'bg-amber-500/15 border-amber-500/30' },
  { key: 'in_production', label: 'In Production', color: 'text-blue-300',   bg: 'bg-blue-500/15 border-blue-500/30' },
  { key: 'completed',     label: 'Completed',     color: 'text-green-300',  bg: 'bg-green-500/15 border-green-500/30' },
];
const STATUS_FLOW = ['Not Accepted', 'Accepted', 'In Production', 'Packing', 'Dispatched'];

const PRIORITY_STYLE = {
  high:    { bg: 'bg-red-500/15 border-red-500/40',    text: 'text-red-300' },
  general: { bg: 'bg-blue-500/10 border-blue-500/30',  text: 'text-blue-300' },
  low:     { bg: 'bg-zinc-800 border-zinc-700',        text: 'text-zinc-400' },
};
const LINE_PALETTE = [
  { bg: 'bg-purple-500/15 border-purple-500/30', text: 'text-purple-300' },
  { bg: 'bg-cyan-500/15 border-cyan-500/30',     text: 'text-cyan-300' },
  { bg: 'bg-emerald-500/15 border-emerald-500/30', text: 'text-emerald-300' },
  { bg: 'bg-pink-500/15 border-pink-500/30',     text: 'text-pink-300' },
  { bg: 'bg-orange-500/15 border-orange-500/30', text: 'text-orange-300' },
  { bg: 'bg-indigo-500/15 border-indigo-500/30', text: 'text-indigo-300' },
];
const lineStyle = (name) => {
  if (!name) return null;
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  return LINE_PALETTE[h % LINE_PALETTE.length];
};

export default function SalesPipeline() {
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState([]);              // server state
  const [pending, setPending] = useState({});         // {id: {field: val}} local pending edits
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [counts, setCounts] = useState({ pending: 0, planning: 0, in_production: 0, completed: 0 });
  const [prodLines, setProdLines] = useState([]);
  const [delayRow, setDelayRow] = useState(null);
  const [linksRow, setLinksRow] = useState(null);
  const [genBusy, setGenBusy] = useState(null);
  const [saveBusy, setSaveBusy] = useState(null);
  // Row ID whose per-item readiness panel is expanded inline (null when all collapsed)
  const [expandedReady, setExpandedReady] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const { deleteState, requestDelete, confirmDelete, cancelDelete } = useAdminDelete(() => { load(tab); loadCounts(); notifySalesOrdersChanged(); });
  const navigate = useNavigate();

  const load = async (bucket = tab) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales/pipeline`, {
        params: { bucket, q, client: clientFilter },
        withCredentials: true,
      });
      setRows(res.data || []);
      setPending({});
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };

  const loadCounts = async () => {
    const entries = await Promise.all(TABS.map(t =>
      axios.get(`${API}/sales/pipeline`, { params: { bucket: t.key }, withCredentials: true })
        .then(r => [t.key, (r.data || []).length]).catch(() => [t.key, 0])
    ));
    setCounts(Object.fromEntries(entries));
  };

  useEffect(() => { load(tab); loadCounts(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    axios.get(`${API}/production/lines`, { withCredentials: true })
      .then(r => setProdLines(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const id = setTimeout(() => load(tab), 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q, clientFilter]);

  // Cross-page sync: reload whenever the Sales Kanban mutates data
  useSalesOrdersSync(() => { load(tab); loadCounts(); });

  // Merge row + pending edits for display
  const view = (r) => ({ ...r, ...(pending[r.id] || {}) });
  const isDirty = (id) => !!pending[id] && Object.keys(pending[id]).length > 0;

  const setField = (row, field, value) =>
    setPending(p => ({ ...p, [row.id]: { ...(p[row.id] || {}), [field]: value } }));

  const saveRow = async (row) => {
    const patch = pending[row.id];
    if (!patch) return;
    setSaveBusy(row.id);
    try {
      await axios.put(`${API}/sales/orders/${row.id}`, patch, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, ...patch } : r));
      setPending(p => { const n = { ...p }; delete n[row.id]; return n; });
      notifySalesOrdersChanged();
      toast.success('Saved');
    } catch { toast.error('Save failed'); }
    finally { setSaveBusy(null); }
  };

  const advanceStatus = async (row) => {
    const idx = STATUS_FLOW.indexOf(row.status);
    if (idx < 0 || idx === STATUS_FLOW.length - 1) return;
    const next = STATUS_FLOW[idx + 1];
    try {
      await axios.patch(`${API}/sales/orders/${row.id}/status`, { status: next }, { withCredentials: true });
      toast.success(`Moved to ${next}`);
      notifySalesOrdersChanged();
      load(tab); loadCounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Status update failed'); }
  };

  const generateInvoice = async (row) => {
    setGenBusy(row.id);
    try {
      const res = await axios.post(`${API}/sales/orders/${row.id}/generate-invoice`, {}, { withCredentials: true });
      toast.success(`Invoice ${res.data.invoice_number} generated`);
      notifySalesOrdersChanged();
      load(tab);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setGenBusy(null); }
  };

  const remove = (row) => {
    requestDelete(
      `/sales/orders/${row.id}`,
      row.voucher_no || 'this order',
      `This will permanently delete SO ${row.voucher_no} and reverse any linked dispatches + FG stock. Cannot be undone.`,
    );
  };

  const logDelay = async () => {
    if (!delayRow) return;
    try {
      await axios.post(`${API}/sales/orders/${delayRow.row.id}/delay-log`, {
        reason: delayRow.reason, delayed_to: delayRow.delayed_to,
      }, { withCredentials: true });
      toast.success('Delay logged');
      setDelayRow(null);
    } catch { toast.error('Failed'); }
  };

  // Toggle per-item readiness from the pipeline table. Mirrors the Kanban
  // behaviour (Sales.jsx) so operators can tick items off without leaving
  // the dense pipeline grid.
  const toggleItemReady = async (orderId, itemIndex) => {
    const order = rows.find(r => r.id === orderId);
    if (!order) return;
    const cur = (order.items_ready || {})[String(itemIndex)];
    const next = !cur?.ready;
    try {
      const res = await axios.patch(
        `${API}/sales/orders/${orderId}/item-ready`,
        { item_index: itemIndex, ready: next },
        { withCredentials: true },
      );
      setRows(prev => prev.map(r => r.id === orderId ? { ...r, items_ready: res.data.items_ready } : r));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="sales-pipeline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Sales Pipeline</h1>
          <p className="text-zinc-500 text-xs mt-0.5">Edit any field inline · hit <span className="text-amber-400 font-semibold">Save</span> to commit · use the <span className="text-amber-400 font-semibold">Links</span> popup to open invoices in Enterprise.</p>
        </div>
        <button onClick={() => navigate('/sales-legacy?new=1')} data-testid="new-order-btn"
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-xl text-xs font-semibold transition-all">
          <Plus size={14} /> New Sales Order
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-xs font-semibold transition-all ${
              tab === t.key ? `${t.bg} ${t.color}` : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'
            }`}>
            {t.label}
            <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.key ? 'bg-black/30' : 'bg-zinc-800'}`}>
              {counts[t.key] || 0}
            </span>
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search voucher, product, code, remark…"
            data-testid="sales-search"
            className="bg-zinc-900 border border-zinc-800 rounded-lg pl-7 pr-3 py-2 text-xs text-zinc-50 w-64 focus:outline-none focus:border-amber-500" />
        </div>
        <div className="relative">
          <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={clientFilter} onChange={e => setClientFilter(e.target.value)} placeholder="Client"
            data-testid="sales-client-filter"
            className="bg-zinc-900 border border-zinc-800 rounded-lg pl-7 pr-3 py-2 text-xs text-zinc-50 w-44 focus:outline-none focus:border-amber-500" />
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1500px]" data-testid="sales-pipeline-table">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-950/50">
                {['SO No', 'Priority', 'Date', 'Party', 'Code', 'Product', 'Qty', 'Prod Due', 'Dispatch Due', 'Prod Line', 'Remark', 'Status', 'Links', 'Save', 'Actions'].map(h => (
                  <th key={h} className="text-left text-zinc-500 text-[10px] font-medium px-3 py-2.5 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={15} className="p-6 text-center text-zinc-500 text-sm">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={15} className="p-10 text-center text-zinc-500 text-sm">No orders in {TABS.find(x => x.key === tab)?.label}</td></tr>}
              {rows.map(orig => {
                const r = view(orig);
                const pstyle = PRIORITY_STYLE[r.priority || 'general'];
                const lstyle = lineStyle(r.production_line);
                const atEnd = STATUS_FLOW.indexOf(r.status) >= STATUS_FLOW.length - 1;
                const dirty = isDirty(r.id);
                const totalItems = (r.items || []).length;
                const doneItems = Object.keys(r.items_ready || {}).filter(k => r.items_ready[k]?.ready).length;
                const isExpanded = expandedReady === r.id;
                return (
                  <React.Fragment key={r.id}>
                  <tr className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${r.is_overdue ? 'bg-red-900/10' : ''} ${dirty ? 'ring-1 ring-amber-500/40' : ''}`} data-testid={`pipeline-row-${r.id}`}>
                    <td className="px-3 py-2.5 font-mono text-amber-300 text-xs font-semibold whitespace-nowrap">
                      {r.so_number || r.voucher_no}
                      {totalItems > 0 && (() => {
                        const all = doneItems === totalItems && totalItems > 0;
                        return (
                          <button
                            type="button"
                            onClick={() => setExpandedReady(isExpanded ? null : r.id)}
                            data-testid={`pipeline-ready-${r.id}`}
                            title={isExpanded ? 'Hide items' : 'Show items to tick ready'}
                            className={`ml-1.5 text-[9px] px-1.5 py-0.5 rounded border font-semibold cursor-pointer hover:brightness-125 transition ${
                              all ? 'bg-green-500/20 text-green-400 border-green-700'
                              : doneItems > 0 ? 'bg-amber-500/20 text-amber-400 border-amber-700'
                              : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                            {all ? '✓' : `${doneItems}/${totalItems}`}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <select value={r.priority || 'general'} onChange={e => setField(orig, 'priority', e.target.value)}
                        data-testid={`priority-${r.id}`}
                        className={`text-[10px] font-semibold rounded-md px-2 py-1 border min-w-[88px] ${pstyle.bg} ${pstyle.text} focus:outline-none cursor-pointer`}>
                        <option value="high">High</option>
                        <option value="general">General</option>
                        <option value="low">Low</option>
                      </select>
                    </td>
                    <td className="px-3 py-2.5 text-zinc-400 text-xs font-mono whitespace-nowrap">{r.date}</td>
                    <td className="px-3 py-2.5 text-zinc-100 text-xs font-medium whitespace-nowrap max-w-[220px] truncate" title={r.client}>{r.client}</td>
                    <td className="px-3 py-2.5 text-zinc-300 text-xs font-mono whitespace-nowrap">{r.product_code || <span className="text-zinc-600 italic">—</span>}</td>
                    <td className="px-3 py-2.5 text-zinc-200 text-xs max-w-[240px] truncate whitespace-nowrap" title={r.product_name}>
                      <span className="inline-flex items-center gap-1.5">
                        <ProductCodeBadge name={r.product_name} code={r.product_code} />
                        <CodeLabel name={r.product_name} />
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-zinc-300 text-xs font-mono whitespace-nowrap">{r.qty} {r.unit}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <input type="date" value={r.production_due_date || ''} onChange={e => setField(orig, 'production_due_date', e.target.value)}
                        data-testid={`prod-due-${r.id}`}
                        className="bg-zinc-950 border border-zinc-800 rounded-md px-1.5 py-1 text-[10px] text-zinc-300 font-mono focus:outline-none focus:border-amber-500 w-32 min-w-[128px]" />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <input type="date" value={r.dispatch_due_date || ''} onChange={e => setField(orig, 'dispatch_due_date', e.target.value)}
                        data-testid={`dispatch-due-${r.id}`}
                        className={`bg-zinc-950 border border-zinc-800 rounded-md px-1.5 py-1 text-[10px] font-mono focus:outline-none focus:border-amber-500 w-32 min-w-[128px] ${r.is_overdue ? 'text-red-400' : 'text-zinc-300'}`} />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <select value={r.production_line || ''} onChange={e => setField(orig, 'production_line', e.target.value)}
                        data-testid={`prod-line-${r.id}`}
                        className={`rounded-md px-2 py-1 text-[10px] font-semibold focus:outline-none w-28 min-w-[112px] border cursor-pointer ${lstyle ? `${lstyle.bg} ${lstyle.text} border-transparent` : 'bg-zinc-950 border-zinc-800 text-zinc-500'}`}>
                        <option value="">— Unassigned —</option>
                        {prodLines.filter(l => l.active !== false).map(l => (
                          <option key={l.id} value={l.name}>{l.name}{l.code ? ` (${l.code})` : ''}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <input value={r.remark || ''} onChange={e => setField(orig, 'remark', e.target.value)}
                        data-testid={`remark-${r.id}`} placeholder="—"
                        className="bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1 text-[10px] text-zinc-200 focus:outline-none focus:border-amber-500 w-40 min-w-[160px]" />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 whitespace-nowrap">{r.status}</span>
                        {!atEnd && (
                          <button onClick={() => advanceStatus(r)} data-testid={`advance-${r.id}`} title="Advance to next phase"
                            className="p-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/30">
                            <ChevronRight size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <button onClick={() => setLinksRow(r)} data-testid={`links-${r.id}`}
                        title="Open links drill-down"
                        className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-[10px] whitespace-nowrap">
                        <Link2 size={11} /> Links
                      </button>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <button onClick={() => saveRow(orig)} disabled={!dirty || saveBusy === r.id}
                        data-testid={`save-${r.id}`}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-all whitespace-nowrap ${dirty ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'}`}>
                        <Save size={11} /> {saveBusy === r.id ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setDelayRow({ row: r, reason: '', delayed_to: '' })}
                          data-testid={`delay-btn-${r.id}`}
                          className="p-1 rounded bg-zinc-800 text-amber-300 hover:bg-zinc-700" title="Log Delay">
                          <AlertTriangle size={11} />
                        </button>
                        {isAdmin && (
                          <button onClick={() => remove(r)} data-testid={`delete-${r.id}`}
                            className="p-1 rounded bg-red-900/30 text-red-400 hover:text-red-300">
                            <Trash2 size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded readiness panel — per-item tickmarks, opens when the count chip is clicked */}
                  {isExpanded && totalItems > 0 && (
                    <tr className="bg-zinc-950/60" data-testid={`pipeline-ready-expanded-${r.id}`}>
                      <td colSpan={15} className="px-4 py-3 border-b border-zinc-800/50">
                        <div className="flex items-center gap-2 text-zinc-400 text-[11px] mb-2">
                          <span className="font-semibold text-zinc-300">Tick items ready:</span>
                          <span className="text-zinc-500">{doneItems}/{totalItems} marked</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-1.5">
                          {(r.items || []).map((it, idx) => {
                            const ready = !!(r.items_ready || {})[String(idx)]?.ready;
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => toggleItemReady(r.id, idx)}
                                data-testid={`pipeline-tick-${r.id}-${idx}`}
                                className={`flex items-start gap-2 px-2 py-1.5 rounded border text-left transition-colors ${ready ? 'bg-green-500/10 border-green-700/40' : 'bg-zinc-900 border-zinc-800 hover:bg-zinc-800/60'}`}
                                title={ready ? 'Click to unmark' : 'Click to mark ready'}>
                                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${ready ? 'bg-green-500 border-green-400' : 'bg-zinc-950 border-zinc-600'}`}>
                                  {ready && <span className="text-black text-[10px] font-bold leading-none">✓</span>}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className={`text-[11px] ${ready ? 'line-through text-zinc-500' : 'text-zinc-200'}`}>
                                    {it.product_name || it.name || '—'}
                                  </div>
                                  <div className="text-zinc-600 text-[10px]">
                                    × {it.qty || it.quantity || 0} {it.unit || ''}
                                    {it.item_type === 'raw' && <span className="ml-1.5 text-emerald-400 uppercase">RM</span>}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {delayRow && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setDelayRow(null)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <h3 className="font-heading font-semibold text-zinc-50 text-sm">Log Delay · {delayRow.row.voucher_no}</h3>
              <button onClick={() => setDelayRow(null)} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Reason</label>
                <textarea rows={3} value={delayRow.reason} onChange={e => setDelayRow(d => ({ ...d, reason: e.target.value }))}
                  data-testid="delay-reason" placeholder="e.g. Raw material shortage…"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">New Dispatch Date</label>
                <input type="date" value={delayRow.delayed_to} onChange={e => setDelayRow(d => ({ ...d, delayed_to: e.target.value }))}
                  data-testid="delay-new-date"
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDelayRow(null)} className="px-4 py-2 bg-zinc-800 text-zinc-300 rounded-lg text-xs">Cancel</button>
                <button onClick={logDelay} data-testid="delay-save" className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-xs font-semibold">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Links Drill-down Popup ═══ */}
      {linksRow && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setLinksRow(null)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="links-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <div>
                <h3 className="font-heading font-semibold text-zinc-50 text-sm">Linked Records</h3>
                <p className="text-zinc-500 text-[10px] mt-0.5 font-mono">{linksRow.voucher_no} · {linksRow.client}</p>
              </div>
              <button onClick={() => setLinksRow(null)} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-2.5">
              {/* Invoice / Enterprise Auto-Fill */}
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt size={14} className="text-amber-400" />
                    <div>
                      <div className="text-zinc-100 text-xs font-semibold">
                        {linksRow.has_invoice ? `Invoice ${linksRow.invoice_number}` : 'No invoice yet'}
                      </div>
                      <div className="text-zinc-500 text-[10px]">Opens in Enterprise — auto-filled with this order's data</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!linksRow.has_invoice && (
                      <button onClick={() => generateInvoice(linksRow)} disabled={genBusy === linksRow.id}
                        data-testid={`gen-invoice-${linksRow.id}`}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-amber-500/20 hover:text-amber-300 text-[10px] font-semibold disabled:opacity-50">
                        <FilePlus2 size={11} /> Generate
                      </button>
                    )}
                    <button onClick={() => navigate(`/enterprise?from_so=${linksRow.id}`)}
                      data-testid={`open-enterprise-${linksRow.id}`}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-[10px] font-semibold">
                      <FileText size={11} /> Open in Enterprise
                    </button>
                  </div>
                </div>
              </div>

              {/* Batches */}
              <DrillRow
                icon={<Factory size={14} className="text-blue-400" />}
                title={linksRow.batches?.length > 0 ? `${linksRow.batches.length} Production Batch${linksRow.batches.length === 1 ? '' : 'es'}` : 'No linked batches'}
                subtitle={(linksRow.batches || []).map(b => `${b.id.slice(0, 8)} · ${b.status}`).join('  ·  ') || 'Create one from Production'}
                ctaLabel="Open Production"
                ctaTestId={`open-production-${linksRow.id}`}
                onClick={() => navigate('/production')}
                disabled={false}
                highlight={linksRow.batches?.length > 0}
              />

              {/* Dispatches */}
              <DrillRow
                icon={<Truck size={14} className="text-green-400" />}
                title={linksRow.dispatches?.length > 0 ? `${linksRow.dispatches.length} Dispatch${linksRow.dispatches.length === 1 ? '' : 'es'}` : 'No dispatches yet'}
                subtitle={(linksRow.dispatches || []).map(d => `${d.id.slice(0, 8)} · ${d.status}`).join('  ·  ') || 'Dispatch will be created on Accept → Dispatched'}
                ctaLabel="Open Dispatch Cal."
                ctaTestId={`open-dispatch-${linksRow.id}`}
                onClick={() => navigate('/dispatch-calendar')}
                disabled={false}
                highlight={linksRow.dispatches?.length > 0}
              />

              {/* Portal origin */}
              <DrillRow
                icon={<Users size={14} className="text-purple-400" />}
                title={linksRow.source?.startsWith('portal') ? 'Portal Order (B2B Client)' : 'Not from portal'}
                subtitle={linksRow.source?.startsWith('portal') ? 'Originated from the B2B client portal' : 'Manually created in ERP'}
                ctaLabel="Open Portal Orders"
                ctaTestId={`open-portal-${linksRow.id}`}
                onClick={() => navigate('/admin/portal-orders')}
                disabled={!linksRow.source?.startsWith('portal')}
                highlight={linksRow.source?.startsWith('portal')}
              />
            </div>
          </div>
        </div>
      )}
      {/* Invoice Modal / Delay Modal / Links Modal above */}
      <ConfirmDeleteModal
        open={deleteState.open}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title={deleteState.title}
        message={deleteState.message}
      />
    </div>
  );
}

function DrillRow({ icon, title, subtitle, ctaLabel, ctaTestId, onClick, disabled, highlight }) {
  return (
    <div className={`rounded-xl border p-3 ${highlight ? 'border-zinc-700 bg-zinc-900' : 'border-zinc-800 bg-zinc-950'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {icon}
          <div className="min-w-0">
            <div className="text-zinc-100 text-xs font-semibold truncate">{title}</div>
            <div className="text-zinc-500 text-[10px] truncate">{subtitle}</div>
          </div>
        </div>
        <button onClick={onClick} disabled={disabled}
          data-testid={ctaTestId}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold whitespace-nowrap ${disabled ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}>
          {ctaLabel} <ChevronRight size={11} />
        </button>
      </div>
    </div>
  );
}
